export function resolvePerlite(state) {
  const result = (key, products = []) => ({ key, products, notes: [] });
  if (state.drainage === 'no') return result('pot_first');
  if (state.drainage !== 'yes') return result('pot_check');
  if (state.condition === 'wet') return result('wet');
  if (state.condition === 'fast' && state.checked !== 'yes') return result('fast');
  if (state.condition === 'fast' && state.goal !== 'water') return result('fast');
  if (state.mix === 'already') return result('premix');
  if (state.mix !== 'plain' || state.condition === 'unknown') return result('observe');
  if (state.condition === 'okay') return result('observe');
  if (state.goal === 'water' && state.plant === 'dry') return result('dry');
  if (state.goal === 'water' && state.plant === 'moist') return result('water', ['vermiculite']);
  if (state.goal === 'air') return result('air', ['perlite']);
  return result('observe');
}

export function resolveHerb(state, { general = false } = {}) {
  const notes = [];
  if (state.place === 'exposed') notes.push('rain');
  const result = (key, products = [], plan = null, soil = null) => ({ key, products, notes, plan, soil });
  if (state.drainage === 'no') return result('pot_first');
  if (state.drainage !== 'yes' && (!general || state.drainage)) return result('pot_check');
  if (state.condition === 'wet') return result('wet');
  if (state.light === 'unknown') return result('light');
  if (state.light !== 'sunny' && (!general || state.light) && ['rosemary','thyme','lemongrass','thai_basil','dill','chilli'].includes(state.plant)) return result('light');
  if (state.place === 'window') notes.push('sun');
  if (state.purpose === 'feeding') return result('feed');
  if (state.condition === 'fast') return result('fast');
  if (state.purpose === 'arrival') return result(state.plant, [], 'arrival');
  if (state.condition === 'unknown') return result('observe');
  if (!['buy','suliflor','premium'].includes(state.mix)) return result('label');
  if (state.purpose === 'have_mix') return state.mix === 'buy' ? result('label') : result(state.plant, [], 'keep', state.mix);
  if (state.purpose === 'repot') return result(state.plant, state.mix === 'buy' ? ['suliflor'] : [], 'repot', state.mix === 'buy' ? 'suliflor' : state.mix);
  return result('observe');
}

// Blank optional fields leave the general guide unchanged; explicit uncertainty still triggers checks.
export function resolveHerbs(plants, state) {
  const conditions = Object.fromEntries(Object.entries(state).filter(([key, value]) => key !== 'plant' && value));
  const groups = new Map();
  for (const plant of new Set(plants)) {
    const answer = resolveHerb({ purpose: 'repot', mix: 'buy', ...conditions, plant }, { general: true });
    const key = JSON.stringify([answer.plan || answer.key, answer.soil, answer.products, answer.notes]);
    if (!groups.has(key)) groups.set(key, { ...answer, plants: [] });
    groups.get(key).plants.push(plant);
  }
  return [...groups.values()];
}

export function mountSoilGuide(guide) {
  if (guide.dataset.ready === 'true') return;
  guide.dataset.ready = 'true';
  const form = guide.querySelector('[data-choice-form]');
  const panel = guide.querySelector('[data-result-panel]');
  const content = guide.querySelector('[data-result-content]');
  const recommendations = guide.querySelector('[data-result-products]');
  const initial = content.cloneNode(true);
  let submitted = false;
  const isHerb = guide.dataset.guide === 'herb';
  const plantError = guide.querySelector('[data-plant-error]');
  const clearResult = () => {
    delete panel.dataset.result;
    content.replaceChildren(...Array.from(initial.childNodes).map(n => n.cloneNode(true)));
    recommendations.replaceChildren();
    guide.querySelectorAll('[data-product]').forEach(card => card.classList.remove('sg-product-relevant'));
  };
  const clone = (selector, target) => {
    const template = guide.querySelector(`template[${selector}]`);
    if (template) target.append(template.content.cloneNode(true));
  };
  const render = () => {
    const data = new FormData(form);
    const state = Object.fromEntries(data);
    const plants = data.getAll('plant');
    if (isHerb && !plants.length) { clearResult(); return false; }
    const answers = isHerb ? resolveHerbs(plants, state) : [resolvePerlite(state)];
    content.replaceChildren();
    if (isHerb) {
      clone('data-multi-intro', content);
      content.querySelector('[data-multi-note]').hidden = plants.length < 2;
    }
    answers.forEach(answer => {
      const group = document.createElement('div');
      group.className = 'sg-result-group';
      if (isHerb) {
        group.dataset.plants = answer.plants.join(',');
        const heading = document.createElement('h4');
        heading.className = 'sg-group-plants';
        heading.textContent = answer.plants.map(plant => guide.querySelector(`template[data-result="${plant}"]`).content.textContent.trim()).join(' · ');
        group.append(heading);
        if (answer.plan) {
          clone(`data-plan="${answer.plan}"`, group);
          const soilTarget = group.querySelector('[data-plan-soil]');
          if (soilTarget) {
            clone(`data-soil="${answer.soil}"`, soilTarget);
            const key = !state.mix || state.mix === 'buy' ? 'new' : 'existing';
            soilTarget.querySelector('[data-plan-choice]').textContent = guide.querySelector(`template[data-plan-text="${key}"]`).content.textContent;
          }
          const next = group.querySelector('[data-plan-next]');
          answer.plants.forEach(plant => {
            const care = document.createElement('section');
            care.className = 'sg-herb-care';
            care.dataset.carePlant = plant;
            const name = document.createElement('h5');
            clone(`data-herb-link="${plant}"`, name);
            care.append(name);
            clone(`data-next="${plant}"`, care);
            next.append(care);
          });
          const feed = group.querySelector('[data-plan-feed]');
          if (feed) feed.textContent = guide.querySelector(`template[data-plan-text="${answer.soil === 'suliflor' ? 'feed_suliflor' : 'feed_existing'}"]`).content.textContent;
        } else {
          clone(`data-result="${answer.key}"`, group);
          clone(`data-herb-action="${answer.key}"`, group);
        }
      } else clone(`data-result="${answer.key}"`, group);
      content.append(group);
    });
    [...new Set(answers.flatMap(answer => answer.notes))].forEach(note => clone(`data-note="${note}"`, content));
    const products = [...new Set(answers.flatMap(answer => answer.products))];
    recommendations.replaceChildren();
    guide.querySelectorAll('[data-product]').forEach(card => {
      card.classList.toggle('sg-product-relevant', products.includes(card.dataset.product));
    });
    products.forEach(key => {
      const card = guide.querySelector(`[data-product="${key}"]`);
      if (!card) return;
      const link = document.createElement('a');
      link.href = `#${card.id}`;
      link.textContent = card.querySelector('[data-product-title]').textContent;
      recommendations.append(link);
    });
    panel.dataset.result = isHerb ? 'herbs' : answers[0].key;
    return true;
  };
  form.hidden = false;
  panel.hidden = false;
  guide.addEventListener('click', event => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    const target = guide.querySelector(`[id="${link.getAttribute('href').slice(1)}"]`);
    const disclosure = target?.closest('details');
    if (disclosure) disclosure.open = true;
  });
  form.addEventListener('submit', event => {
    event.preventDefault();
    if (isHerb && !new FormData(form).getAll('plant').length) {
      plantError.hidden = false;
      form.querySelector('input[name="plant"]').focus();
      clearResult();
      return;
    }
    if (plantError) plantError.hidden = true;
    submitted = true;
    render();
    panel.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest' });
  });
  const selectedHerb = guide.querySelector('[data-selected-herb]');
  const showPlant = () => {
    if (!selectedHerb) return;
    const plants = new FormData(form).getAll('plant');
    selectedHerb.textContent = guide.querySelector('template[data-selection-count]').content.textContent.replace('[count]', plants.length);
    selectedHerb.hidden = !plants.length;
    if (plants.length) plantError.hidden = true;
  };
  form.addEventListener('change', () => {
    showPlant();
    if (submitted && form.checkValidity()) render();
  });
  form.addEventListener('reset', () => {
    submitted = false;
    if (selectedHerb) { selectedHerb.replaceChildren(); selectedHerb.hidden = true; }
    if (plantError) plantError.hidden = true;
    const adjustments = form.querySelector('[data-adjustments]');
    if (adjustments) adjustments.open = false;
    clearResult();
  });
  guide.querySelectorAll('[data-product]').forEach(card => {
    card.id = `${guide.id}-product-${card.dataset.product}`;
    const select = card.querySelector('[data-variant-select]');
    if (!select) return;
    select.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      const add = card.querySelector('button[type="submit"]');
      card.querySelector('[data-product-price]').textContent = option.dataset.price;
      add.disabled = option.dataset.available !== 'true';
      add.textContent = add.disabled ? add.dataset.unavailableLabel : add.dataset.addLabel;
      card.querySelectorAll('[data-product-link]').forEach(link => {
        const url = new URL(link.href);
        url.searchParams.set('variant', option.value);
        link.href = url.toString();
      });
    });
  });
  const params = new URLSearchParams(location.search);
  const preview = params.get('preview_theme_id');
  if (params.get('view')?.startsWith('gp-')) {
    guide.querySelectorAll('[data-guide-link]').forEach(link => {
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.set('view', link.dataset.guideLink);
      link.href = url.toString();
    });
  }
  if (preview) guide.querySelectorAll('a[href]').forEach(link => {
    const url = new URL(link.href);
    if (url.origin === location.origin && !link.getAttribute('href').startsWith('#')) {
      url.searchParams.set('preview_theme_id', preview);
      link.href = url.toString();
    }
  });
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('gp-soil-guide').forEach(mountSoilGuide);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('gp-soil-guide').forEach(mountSoilGuide));
}
